import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ListItemProps {
  to?: string;
  onClick?: () => void;
  leading?: ReactNode;
  title: string;
  subtitle?: string;
  trailing?: ReactNode;
  divider?: boolean;
  className?: string;
  /**
   * Overrides the title's default single-line `truncate` behavior. Pass e.g. `'line-clamp-2
   * break-words'` when the title text is spec-mandated verbatim (so it cannot be shortened)
   * and must remain fully legible rather than being cut off with an ellipsis on narrow
   * viewports. Defaults to `'truncate'` (unchanged behavior for every existing caller).
   */
  titleClassName?: string;
  'data-testid'?: string;
}

export function ListItem({
  to,
  onClick,
  leading,
  title,
  subtitle,
  trailing,
  divider = true,
  className,
  titleClassName,
  'data-testid': testId,
}: ListItemProps) {
  const content = (
    <>
      {leading && <div className="shrink-0">{leading}</div>}
      <div className="min-w-0 flex-1">
        <p className={cn('text-sm font-medium text-foreground', titleClassName ?? 'truncate')}>{title}</p>
        {subtitle && (
          <p className="mt-0.5 text-xs text-muted-foreground truncate">{subtitle}</p>
        )}
      </div>
      {trailing && <div className="shrink-0">{trailing}</div>}
      {to && <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
    </>
  );

  const sharedClasses = cn(
    'flex items-center gap-3 px-[var(--page-padding-x)] py-3 min-h-[48px]',
    'transition-colors',
    (to || onClick) && 'hover:bg-accent cursor-pointer',
    divider && 'border-b border-border last:border-b-0',
    className,
  );

  if (to) {
    return (
      <Link to={to} className={sharedClasses} data-testid={testId}>
        {content}
      </Link>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(sharedClasses, 'w-full text-left')}
        data-testid={testId}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={sharedClasses} data-testid={testId}>
      {content}
    </div>
  );
}
